const DEBUG = false
/**
 * Logs messages to the console if DEBUG is true.
 * @param  {...any} args - Arguments to log.
 */
function log(...args) {
  if (DEBUG) {
    console.log(...args)
  }
}
/**
 * Checks the availability of various storage options in the browser.
 * @returns {Object} - Object containing availability of sessionStorage, localStorage, IndexedDB, and WebSQL.
 */
async function checkStorageAvailability() {
  const sessionStorageAvailable = testStorage(sessionStorage)
  const localStorageAvailable = testStorage(localStorage)
  const indexedDBAvailable = !!window.indexedDB
  const openDatabaseAvailable = !!window.openDatabase
  return { sessionStorageAvailable, localStorageAvailable, indexedDBAvailable, openDatabaseAvailable }
}
/**
 * Utility function to test storage availability.
 * @param {Storage} storageType - The storage type to test.
 * @returns {boolean} - Returns true if storage is available, false otherwise.
 */
function testStorage(storageType) {
  try {
    const testKey = 'irqubtup-dyggftxj'
    storageType.setItem(testKey, testKey)
    storageType.removeItem(testKey)
    return true
  }
  catch (e) {
    return false
  }
}
async function getGraphicsAndMultimediaFeatures() {
  const canvas = document.createElement('canvas')
  const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl')
  let webglRenderer = 'Not supported'
  let webglVendor = 'Not supported'
  let webglExtensionsLength = 0
  try {
    if (gl) {
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info')
      webglRenderer = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'Unavailable'
      webglVendor = debugInfo ? gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL) : 'Unavailable'
      webglExtensionsLength = gl.getSupportedExtensions().length
    }
    const canvasFingerprint = getCanvasFingerprint(canvas)
    const audioContextFingerprint = await getAudioContextFingerprint()
    return {
      webglRenderer,
      webglVendor,
      webglExtensionsLength,
      canvasFingerprint,
      audioContextFingerprint,
    }
  }
  catch (error) {
    log('Error collecting multimedia features:', error)
    return {
      webglRenderer,
      webglVendor,
      webglExtensionsLength,
    }
  }
}
function getCanvasFingerprint(canvas) {
  const ctx = canvas.getContext('2d')
  if (!ctx) {
    log('2D context not available.')
    return null
  }
  const txt = 'canvas fingerprint'
  ctx.textBaseline = 'top'
  ctx.font = '14px \'Arial\''
  ctx.textBaseline = 'alphabetic'
  ctx.fillStyle = '#f60'
  ctx.fillRect(125, 1, 62, 20)
  ctx.fillStyle = '#069'
  ctx.fillText(txt, 2, 15)
  ctx.fillStyle = 'rgba(102, 204, 0, 0.7)'
  ctx.fillText(txt, 4, 17)
  return canvas.toDataURL()
}
async function getAudioContextFingerprint() {
  const AudioContext = window.AudioContext || window.webkitAudioContext
  if (!AudioContext) {
    return 'Not supported'
  }
  try {
    const context = new AudioContext()
    const oscillator = context.createOscillator()
    oscillator.type = 'triangle'
    oscillator.frequency.setValueAtTime(10000, context.currentTime)
    const compressor = context.createDynamicsCompressor()
    const settings = ['threshold', 'knee', 'ratio', 'reduction', 'attack', 'release'].map(prop => {
      return compressor[prop].value
    })
    oscillator.connect(compressor)
    compressor.connect(context.destination)
    oscillator.start(0)
    oscillator.stop(0)
    context.close()
    return settings.join(',')
  }
  catch (e) {
    return 'Access Denied or Not Available'
  }
}
function getTouchSupport() {
  let maxTouchPoints = 0
  let touchEvent = false
  if (navigator.maxTouchPoints) {
    maxTouchPoints = navigator.maxTouchPoints
    touchEvent = true
  }
  else if (window.TouchEvent) {
    touchEvent = true
  }
  return { maxTouchPoints, touchEvent }
}
function getGamepadsSupport() {
  let gamepadsConnected = 0
  if (navigator.getGamepads) {
    const gamepads = navigator.getGamepads()
    gamepadsConnected = Array.from(gamepads)
      .filter(Boolean).length
  }
  return gamepadsConnected
}
function getUserInteractionAndAccessibilityFeatures() {
  const touchSupport = getTouchSupport()
  const gamepadsSupport = getGamepadsSupport()
  return {
    touchSupport,
    gamepadsSupport,
  }
}
async function collectBrowserInfo() {
  const browserInfo = {
    userAgent: navigator.userAgent,
    acceptLanguage: navigator.language || navigator.languages && navigator.languages[0],
    platform: navigator.platform,
    doNotTrack: navigator.doNotTrack || window.doNotTrack || navigator.msDoNotTrack,
    colorDepth: screen.colorDepth,
    pixelDepth: screen.pixelDepth,
    cpuClass: Math.ceil(navigator.hardwareConcurrency / 4),
    deviceMemory: Math.round(navigator.deviceMemory),
  }
  return browserInfo
}

/**
 * Collects various browser information and hashes it.
 * @returns {Promise<string>} - A promise that resolves to the hash of the browser information.
 */
async function collectAndHash() {
  const browserInfo = await collectBrowserInfo()
  const storageAvailability = await checkStorageAvailability()
  const graphicsAndMultimediaFeatures = await getGraphicsAndMultimediaFeatures()
  const userInteractionAndAccessibilityFeatures = await getUserInteractionAndAccessibilityFeatures()

  const results = await Promise.all([
    browserInfo,
    storageAvailability,
    graphicsAndMultimediaFeatures,
    userInteractionAndAccessibilityFeatures,
  ])

  const combinedResult = results.map(obj => flattenObject(obj)).join('&')
  return combinedResult
}
export { collectAndHash }

function flattenObject(obj, prefix = '') {
  return Object.entries(obj).map(([key, value]) => {
    if (typeof value === 'object' && value !== null) {
      return flattenObject(value, `${prefix}${key}.`)
    } 
    else {
      return `${prefix}${key}=${value}`
    }
  }).join('&')
}
